<template>
  <div class="risk-report">
    <a
      href="#risk-report-detail"
      class="is-flex"
      @click="toggleOpen"
      :aria-expanded="isOpen.toString()"
      aria-controls="risk-report-detail"
    >
      <strong>Risk Report</strong>
      <NrIcon
        :name="isOpen ? 'chevron-down' : 'chevron-up'"
        class="m-left-4 align-center"
      />
    </a>
    <div id="risk-report-detail" :class="isOpen ? '' : 'is-hidden'">
      <div class="risk-report-row">
        <NrCard
          v-for="(item, index) in severityData"
          :key="`card-${index}-${item.value}`"
          class="is-inline-flex"
        >
          <strong class="is-size-6">
            {{ item.label }}
          </strong>
          <nr-tag
            class="card-tag"
            :label="`${item.value.toLocaleString('en-US')}`"
            :tag-color="item.active_color"
            size="normal"
            :badge="true"
          />
        </NrCard>
        <NrBar :values="severityData"></NrBar>
      </div>
      <div class="risk-report-row">
        <NrCard
          v-for="(item, index) in reviewStatusData"
          :key="`card-${index}-${item.value}`"
          class="is-inline-flex"
        >
          <strong class="is-size-6">
            {{ item.label }}
          </strong>
          <nr-tag
            class="card-tag"
            :label="`${item.value.toLocaleString('en-US')}`"
            :tag-color="item.active_color"
            size="normal"
            :badge="true"
          />
        </NrCard>
        <NrBar :values="reviewStatusData"></NrBar>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
/* eslint-disable camelcase */
import NrBar from '@nextrequestco/components/src/components/NrBar'
import NrCard from '@nextrequestco/components/src/components/NrCard'
import NrIcon from '@nextrequestco/components/src/components/NrIcon'
import NrTag from '@nextrequestco/components/src/components/NrTag'
import { mapActions, mapState } from 'vuex'
import {
  DocumentSeverity,
  humanizedSeverityStatus,
  severityStatusColor,
  severityStatusBgColor
} from '../../admin/document_severity_status'
import {
  DocumentReviewStatus,
  humanizedReviewStatus,
  reviewStatusColor,
  reviewStatusBgColor
} from '../../admin/document_review_status'

export default {
  name: 'RiskReport',
  components: {
    NrBar,
    NrCard,
    NrIcon,
    'nr-tag': NrTag
  },
  props: {
    documents: {
      type: Array,
      default: () => []
    },
    filterParams: {
      type: Object,
      default: () => ({})
    },
    selectAll: {
      type: Boolean,
      default: () => false
    }
  },
  mounted() {
    if (this.selectAll) {
      this.fetchRequestDocumentRisk(this.filterParams)
    }
  },
  data() {
    return {
      isOpen: true
    }
  },
  computed: {
    ...mapState('request_show', ['request', 'requestDocumentRisk']),
    documentRiskCounts() {
      return {
        high: this.requestDocumentRisk.high_severity || 0,
        medium: this.requestDocumentRisk.medium_severity || 0,
        low: this.requestDocumentRisk.low_severity || 0,
        na: this.requestDocumentRisk.unscannable || 0,
        pending:
          (this.requestDocumentRisk.total_documents || 0) -
          (this.requestDocumentRisk.total_scanned || 0),
        reviewed: this.requestDocumentRisk.reviewed || 0,
        needs_review: this.requestDocumentRisk.needs_review || 0
      }
    },
    severityData() {
      const severities: (DocumentSeverity | null)[] = [
        'high',
        'medium',
        'low',
        'na',
        null
      ]
      return severities.map((severity) => {
        let value
        if (this.selectAll) {
          value = this.documentRiskCounts[severity || 'pending']
        } else {
          value = this.documents.filter(
            ({ document_scan: { severity: docSeverity } }) =>
              docSeverity === severity
          ).length
        }
        return {
          label: humanizedSeverityStatus(severity),
          color: severityStatusBgColor(severity),
          active_color: severityStatusColor(severity),
          value
        }
      })
    },
    reviewStatusData() {
      const reviewed = this.documents.filter(
        ({ document_scan: { review_status } }) => review_status === 'reviewed'
      ).length
      const statuses: DocumentReviewStatus[] = ['needs_review', 'reviewed']
      return statuses.map((status) => {
        let value
        if (this.selectAll) {
          value = this.documentRiskCounts[status] || 0
        } else {
          value =
            status === 'reviewed' ? reviewed : this.documents.length - reviewed
        }
        return {
          label: humanizedReviewStatus(status),
          color: reviewStatusBgColor(status),
          active_color: reviewStatusColor(status),
          value
        }
      })
    }
  },
  methods: {
    ...mapActions('request_show', ['fetchRequestDocumentRisk']),
    toggleOpen() {
      this.isOpen = !this.isOpen
    }
  }
}
</script>

<style scoped lang="scss">
.risk-report {
  margin: 0.75rem 0;

  .risk-report-row {
    margin: 0.75rem 0;

    &:last-child {
      margin-bottom: 0;
    }
  }
  .align-center {
    display: flex;
    align-items: center;
  }
  .tag-element {
    margin-left: 0.25rem;
    position: static;
  }

  .nrcard {
    margin-top: 0.5rem;
    margin-bottom: 0;

    &:not(:last-child) {
      margin-right: 0.5rem;
    }
  }

  .nrbar {
    margin-top: 0.5rem;
  }
}
</style>
